/**
 * The cut profile of geschlitzter Schaumstoff (Nuten), as numbers.
 *
 * Read from two places, like `noppenSurface`: `createSlottedPadGeometry` builds
 * the grooves, and `FoamMaterials` paints them onto pads with cutouts, whose
 * extruded caps cannot be grooved. Both have to agree on what a groove is.
 *
 * A groove is what a saw or a mill leaves: straight-walled and flat-bottomed.
 * The old texture drew a 20 mm sine wave instead, which is a corrugation, not
 * a cut.
 */

/**
 * The sheet this is: Adam Hall 019615, "Plastazote (LD29) mit 7 mm Nuten
 * 15 mm". Closed-cell, physically cross-linked polyethylene foam, black, at
 * 29 kg/m³ with 0.65 mm cells; a 15 mm sheet with 7 mm grooves cut 10 mm deep
 * between 14 mm ribs, made to take 6–7 mm partition walls. The figures below
 * are the retailer datasheets' (aweo.de, ltt-versand.de); the manufacturer's
 * own page names only the groove width and the thickness.
 */

/** Breite einer Nut, in mm — the "7 mm" in the product's name. */
export const SLOT_WIDTH_MM = 7;

/** Breite eines Stegs zwischen zwei Nuten, in mm. */
export const SLOT_RIB_MM = 14;

/** Abstand zweier Nuten, in mm: a rib and a groove. */
export const SLOT_PITCH_MM = SLOT_WIDTH_MM + SLOT_RIB_MM;

/**
 * Tiefe einer Nut, in mm.
 *
 * A fixed depth, not a floor thickness: the groove has to hold a partition
 * wall, so the sheet is cut to the same depth however thick it is. A 15 mm
 * sheet keeps 5 mm under the grooves.
 */
export const SLOT_DEPTH_MM = 10;

/**
 * Thinnest floor a pad may keep under its grooves, in mm. Below it the pad is
 * not this sheet, and is built as a plain block.
 */
export const SLOT_MIN_FLOOR_MM = 3;

/**
 * How dark the inside of a groove goes, as a fraction of the ambient light
 * taken away. See `SPIKE_OCCLUSION_STRENGTH` for why occlusion has to be
 * computed here at all.
 *
 * Lower than the nubs' figure: in the product photograph the groove floors
 * read plainly, a step darker than the ribs rather than black, because a 7 mm
 * slit under soft light is still lit from a good part of the sky and the pale
 * cell walls inside it scatter what arrives.
 */
export const SLOT_OCCLUSION_STRENGTH = 0.6;

/**
 * Sky visible from a groove wall at depth `t` below the face, in [0, 1].
 *
 * A wall point looks across the groove at the opposite wall, and sees the sky
 * only through the slit above: from the elevation of the opposite wall's top
 * edge, `atan(t / width)`, up to straight along its own wall. Weighted by the
 * cosine to its own normal and normalised over its hemisphere that is
 * `(1 - sin θ) / 2` — half the sky at the very top edge, next to nothing a few
 * millimetres down a 4 mm groove, which is right: a slit that narrow is black.
 *
 * Two-dimensional, since the groove is far longer than it is wide.
 */
export function slotWallVisibility(depth: number, width: number): number {
  const sine = depth / Math.hypot(depth, width);
  return (1 - sine) / 2;
}

/**
 * Sky visible from the floor of a groove `depth` deep and `width` wide.
 *
 * From the floor the slit subtends `atan(width / 2 / depth)` either side of
 * vertical; cosine-weighted and normalised that is the sine of that angle.
 */
export function slotFloorVisibility(depth: number, width: number): number {
  const half = width / 2;
  return half / Math.hypot(depth, half);
}

/** A visibility scaled by {@link SLOT_OCCLUSION_STRENGTH} into the shade. */
export function slotShade(visibility: number): number {
  return 1 - SLOT_OCCLUSION_STRENGTH * (1 - visibility);
}
